import { Link } from 'react-router-dom'
import type { LinkProps } from 'react-router-dom'
import type { ComponentProps, ReactNode } from 'react'
import { Spinner } from '@/components/ui/Spinner'
import { buttonStyles } from '@/components/ui/styles'
import type { ButtonSize, ButtonVariant } from '@/components/ui/styles'

type Shared = {
  variant?: ButtonVariant
  size?: ButtonSize
  fullWidth?: boolean
  leadingIcon?: ReactNode
  trailingIcon?: ReactNode
  className?: string
  children?: ReactNode
}

// ComponentProps (not ButtonHTMLAttributes) so `ref` is part of the public prop set —
// React 19 passes it as an ordinary prop, so no forwardRef is involved anywhere here.
type ButtonProps = Shared &
  Omit<ComponentProps<'button'>, keyof Shared> & {
    /** Shows a spinner, blocks clicks, and marks the control busy for assistive tech. */
    isLoading?: boolean
    /** Replaces the label while loading, e.g. "Saving…". */
    loadingText?: string
  }

export function Button({
  variant,
  size,
  fullWidth,
  leadingIcon,
  trailingIcon,
  isLoading = false,
  loadingText,
  className,
  children,
  disabled,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      // Default to "button": an unlabelled submit inside a form is a classic
      // accidental-submit bug.
      type={type}
      className={buttonStyles({ variant, size, fullWidth, className })}
      disabled={disabled ?? isLoading}
      aria-busy={isLoading || undefined}
      {...rest}
    >
      {isLoading ? <Spinner label={null} /> : leadingIcon}
      {isLoading && loadingText ? loadingText : children}
      {!isLoading && trailingIcon}
    </button>
  )
}

type LinkButtonProps = Shared & Omit<LinkProps, keyof Shared>

/** A router link that looks like a button. Navigation must stay a real anchor. */
export function LinkButton({
  variant,
  size,
  fullWidth,
  leadingIcon,
  trailingIcon,
  className,
  children,
  ...rest
}: LinkButtonProps) {
  return (
    <Link className={buttonStyles({ variant, size, fullWidth, className })} {...rest}>
      {leadingIcon}
      {children}
      {trailingIcon}
    </Link>
  )
}
